import { Dialog, Transition } from '@headlessui/react'
import { MenuIcon } from '@heroicons/react/outline'
import { XIcon } from '@heroicons/react/solid'
import clsx from 'clsx'
import ExtLink from 'components/common/ExtLink'
import IntLink from 'components/common/IntLink'
import DiscountIcon from 'components/icons/Discount2'
import PhoneGIcon from 'components/icons/PhoneGIcon'
import TelegramIcon from 'components/icons/TelegramIcon'
import HeaderAddress from './HeaderAddress'
import maxIcon from 'public/img/maxIcon.png'
import Image from 'next/image'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import useSlug from 'hooks/useSlug'
import { isPresent, isSame, isSamePath } from 'lib/utils'

import { useRouter } from 'next/router'
import { Fragment, useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { IMenuItem, selectCities } from 'store/api'
import { selectCity } from 'store/auth'
import {
  TELEGRAM_HEAD_LINK,
  MAX_HEAD_LINK,
} from 'config/constants'

type Props = {
  drawerMenu?: IMenuItem[]
  leftMenu?: IMenuItem[]
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  isOpen: boolean
  onOpenCallback: () => void
  onOpenAddressModal: () => void;
  deliveryAddress: string;
  setIsOpenCb: React.Dispatch<React.SetStateAction<boolean>>
}

let HeaderMobileMenu = ({
  drawerMenu,
  leftMenu,
  setIsOpen,
  isOpen,
  onOpenCallback,
  onOpenAddressModal,
  deliveryAddress,
  setIsOpenCb,
}: Props) => {
  let {
    router: { asPath },
    queryLng,
  } = useSlug()
  let router = useRouter()

  const callbackPending = useRef(false)

  let [isCategoryListOpen, setIsCategoryListOpen] = useState(false)

  let activeCities = useSelector(selectCities(queryLng))
  let _city = useSelector(selectCity)
  let city = isPresent(_city)
    ? activeCities.find((c) => c.code === _city)
    : null

  useIsomorphicLayoutEffect(() => {
    setIsCategoryListOpen(false)
  }, [isOpen])

  useIsomorphicLayoutEffect(() => {
    setIsOpen(false)
  }, [asPath])

  const onDrawerMenuItemClick = useCallback(
    (e, path: string) => {
      if (isSamePath(asPath, path)) {
        setIsOpen(false)
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [asPath]
  )

  return (
    <>
      <div className='flex'>
        <button
          type='button'
          aria-label='Меню'
          className='-m-2 p-3.5 outline-none focus:outline-none rounded-full bg-lightGrey'
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {!isOpen ? (
            <MenuIcon className='h-5 w-5' aria-hidden='true' />
          ) : (
            <XIcon className='h-5 w-5' aria-hidden='true' />
          )}
        </button>
      </div>

      <Transition.Root
        show={isOpen}
        as={Fragment}
        afterLeave={() => {
          if (callbackPending.current) {
            callbackPending.current = false
            onOpenCallback()
          }
        }}
      >
        <Dialog
          as='div'
          className='fixed inset-0 flex z-30 lg:hidden select-none overflow-y-auto'
          onClose={() => { }}
        >
          <Transition.Child
            as={Fragment}
            enter='transition-opacity ease-linear duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='transition-opacity ease-linear duration-300'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <Dialog.Overlay className='fixed inset-0 bg-dark bg-opacity-75 absolute top-[85px] left-0' />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter='transition ease-in-out duration-300 transform'
            enterFrom='-translate-x-full'
            enterTo='translate-x-0'
            leave='transition ease-in-out duration-300 transform'
            leaveFrom='translate-x-0'
            leaveTo='-translate-x-full'
          >
            <div className='h-full absolute top-[85px] left-0 sm:max-w-xs w-full text-lg leading-snug bg-white shadow-xl pb-4 flex flex-col overflow-y-auto'>
              <ul className='pt-5 px-5 space-y-6'>
                <li className='inline-block -ml-3 mb-4'>
                  <HeaderAddress
                    deliveryAddress={deliveryAddress}
                    onOpenModal={() => (onOpenAddressModal(), setIsOpen(false))}
                    className='border border-outline w-auto'
                    setIsOpenCb={setIsOpenCb}
                  />
                </li>
                <li
                  className={clsx(
                    'overflow-hidden transition-max-height flow-root',
                    {
                      'max-h-full': isCategoryListOpen,
                      'max-h-4 pb-5': !isCategoryListOpen,
                    }
                  )}
                >
                  <button
                    type='button'
                    onClick={(e) => {
                      e.preventDefault()
                      setIsCategoryListOpen((prev) => !prev)
                    }}
                    className={clsx(
                      '-m-2 p-2 block tracking-tight outline-none focus:outline-none text-dark2',
                      {
                        'text-accent':
                          router.pathname.startsWith('/categories') ||
                          router.pathname.startsWith('/product'),
                      }
                    )}
                  >
                    Меню доставки
                  </button>

                  <ul className='ml-0.5 pl-4'>
                    {leftMenu?.map(({ slug, name }) => (
                      <li key={slug}>
                        <IntLink
                          href={`/categories/${slug}`}
                          className='-m-2 mt-3 p-2 block tracking-tight'
                        >
                          {name}
                        </IntLink>
                      </li>
                    ))}
                  </ul>
                </li>

                {drawerMenu?.map((x) => (
                  <li key={x.path} className='flow-root'>
                    <span
                      className={clsx(
                        '-m-2 p-2 block tracking-tight text-dark2',
                        {
                          'text-accent': isSame(
                            router.pathname,
                            x.path
                          ),
                        }
                      )}
                    >
                      {x.path.startsWith('http') ? (
                        <ExtLink href={x.path}>{x.name}</ExtLink>
                      ) : (
                        <IntLink
                          href={x.path}
                          className='flex items-center'
                          onClick={(e) =>
                            onDrawerMenuItemClick(e, x.path)
                          }
                        >
                          {x.name}
                          {isSame(x.path, '/aktsii') && (
                            <div className='ml-2'>
                              <DiscountIcon className='h-5 w-5' />
                            </div>
                          )}
                        </IntLink>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              {isPresent(city) && (
                <div className='pt-12 px-5 text-dark2'>
                  {city.phones.map((phone) => {
                    return (
                      <a
                        key={phone}
                        href={`tel:+${phone.replace(/\D/g, '')}`}
                      >
                        <div
                          key={phone}
                          className='flex gap-4 items-center leading-relaxed mb-4'
                        >
                          <PhoneGIcon className='w-7 h-7 rounded' />
                          <span>{phone}</span>
                        </div>
                      </a>
                    )
                  })}
                  <ExtLink
                    href={TELEGRAM_HEAD_LINK}
                    className='flex gap-4 items-center leading-relaxed mb-4'
                  >
                    <TelegramIcon className='w-7 h-7 rounded' />
                    <span className=''>
                      Telegram
                    </span>
                  </ExtLink>
                  <ExtLink
                    href={MAX_HEAD_LINK}
                    className='flex gap-4 items-center leading-relaxed mb-4'
                  >
                    <Image
                      src={maxIcon}
                      layout='fixed'
                      objectFit='contain'
                      width='28px'
                      height='28px'
                      alt=''
                    />
                    <span className=''>
                      MAX
                    </span>
                  </ExtLink>
                </div>
              )}
              <div className='px-5 pt-2 pb-8'>
                <button
                  type='button'
                  data-testid='callback-open-mobile'
                  className='btn btn-primary h-12 w-full rounded-md text-base font-semibold flex items-center justify-center gap-2'
                  onClick={() => {
                    callbackPending.current = true
                    setIsOpen(false)
                  }}
                >
                  {/* Как в приложении: трубка слева и подпись «Обратный звонок» */}
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.8} strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                    <path d='M7 3 4 4c-2 1 0 7 4 11s10 6 11 4l1-3-5-3-2 2c-2-1-3-2-4-4l2-2Z' />
                  </svg>
                  Обратный звонок
                </button>
              </div>
            </div>
          </Transition.Child>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default HeaderMobileMenu
